import { RefObject, useEffect, memo, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Message } from '../types';
import { HeroSection } from './HeroSection';
import { useMessageAnimation } from '../hooks/useMessageAnimation';
import type { RendererResult } from '../utils/renderers/types';

interface TerminalContentProps {
  messages: Message[];
  isTyping: boolean;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  onCommand: (command: string) => void;
  isInitializing: boolean;
  onCTAClick: (type: 'early-access' | 'get-started') => void;
  onAnimationStateChange?: (isAnimating: boolean) => void;
}

// Helper function to get system message text color class
function getSystemMessageClassName(message: Message): string {
  if (message.isThinking) {
    return 'text-zinc-400';
  }
  if (message.isError) {
    return 'text-red-400';
  }
  return 'text-gray-300';
}

// Helper function to sanitize content
function sanitizeContent(content: string): string {
  return DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
}

// Helper function to get message key
function getMessageKey(message: Message, index: number): string {
  return message.id || `message-${index}-${message.timestamp.getTime()}`;
}

// Helper function to get fade-in style
function getFadeInStyle(
  animationType: string | undefined,
  progress: number | undefined
): React.CSSProperties | undefined {
  if (animationType === 'fade-in') {
    return {
      opacity: progress ? progress / 100 : 1,
      transition: 'opacity 0.1s ease-in',
    };
  }
  return undefined;
}

// Component for rendering user messages
function UserMessage({
  message,
  sanitizedContent,
}: {
  readonly message: Message;
  readonly sanitizedContent: string;
}) {
  const prefixClassName = message.isChatMessage
    ? 'text-purple-400'
    : 'text-cyan-400';
  const prefix = message.isChatMessage ? '›' : '$';
  const content = message.isChatMessage
    ? sanitizedContent
    : `mifta ${sanitizedContent}`;

  return (
    <div className="flex gap-2">
      <span className={prefixClassName}>{prefix}</span>
      <span className="text-white">{content}</span>
    </div>
  );
}

// Component for rendering system messages
function SystemMessage({
  message,
  sanitizedContent,
  renderResult,
}: {
  readonly message: Message;
  readonly sanitizedContent: string;
  readonly renderResult: RendererResult;
}) {
  const className = `whitespace-pre-wrap ${getSystemMessageClassName(message)}`;
  const style = getFadeInStyle(message.animation?.type, renderResult.progress);

  return (
    <div className={className} style={style}>
      {sanitizedContent}
      {message.isThinking && (
        <span className="inline-block ml-0.5 animate-dots">...</span>
      )}
    </div>
  );
}

export const TerminalContent = memo(function TerminalContent({
  messages,
  isTyping,
  messagesEndRef,
  onCommand,
  isInitializing,
  onCTAClick,
  onAnimationStateChange,
}: TerminalContentProps) {
  // Determine background color: darker only during initialization
  const shouldDarken = useMemo(() => isInitializing, [isInitializing]);

  // Use animation hook for managing message animations
  const { isAnimating, getRenderResult } = useMessageAnimation(messages);

  // Notify parent about animation state changes
  useEffect(() => {
    onAnimationStateChange?.(isAnimating);
  }, [isAnimating, onAnimationStateChange]);

  return (
    <div
      className={`flex-1 overflow-y-auto p-6 md:p-8 space-y-4 font-mono text-sm scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-700 transition-colors duration-500 ${
        shouldDarken ? 'bg-[#141414]' : 'bg-[#1a1a1a]'
      }`}
    >
      {messages.length === 0 ? (
        <HeroSection onCommand={onCommand} onCTAClick={onCTAClick} />
      ) : (
        <div className="space-y-4">
          {messages.map((message, index) => {
            const renderResult = getRenderResult(message);
            const sanitizedContent = sanitizeContent(
              renderResult.displayContent
            );
            const messageKey = getMessageKey(message, index);

            return (
              <div key={messageKey} className="space-y-2">
                {message.type === 'user' ? (
                  <UserMessage
                    message={message}
                    sanitizedContent={sanitizedContent}
                  />
                ) : (
                  <SystemMessage
                    message={message}
                    sanitizedContent={sanitizedContent}
                    renderResult={renderResult}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {isTyping && (
        <div className="flex items-center gap-2 text-gray-500">
          <span className="animate-pulse">▊</span>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
});
